import React, { useEffect, useState, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { Link } from "react-router-dom";
import CommonModal from "./CommonModal";
import Confirm from "./Confirm";
import { Utils } from "../utils";
import { Number } from "../controls";
import { usePersistStore, useStore } from "../store";
import { ArtaService } from "../services";
import { useCreateBid } from "../hooks/queries";

interface CommonModalProps {
  ad: any;
  seller: any;
  slidesData: any[];
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const PlaceYourBidModal: React.FC<CommonModalProps> = ({
  show,
  onHide,
  onSuccess,
  ad,
  seller,
  slidesData,
}) => {
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [price, setPrice] = useState<string>();
  const { token, user } = usePersistStore((s: any) => s);
  const { setIsLoading, setErrorMessage } = useStore((s: any) => s);
  const [validationError, setValidationError] = useState<string>("");
  const [quote, setQuote] = useState<any>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const createBid = useCreateBid();

  const getQuote = useCallback(async () => {
    const result = await ArtaService.getQuote(token, {
      adId: ad?._id,
    });
    if (result?.error) {
      setValidationError(result?.error);
      setIsLoading(false);
      return;
    }
    if (result?.data) {
      setQuote(result?.data);
    }
  }, [ad, token, setValidationError, setIsLoading]);

  useEffect(() => {
    if (!ad || !ad?._id || !token) return;
    getQuote();
  }, [ad, token, getQuote]);

  const handleClose = () => {
    setValidationMessage("");
    setPrice("");
    setAcceptedTerms(false);
    onHide();
  };

  const handleSubmitClick = () => {
    if (!ad || !ad?._id) return;
    if (!price) return setValidationMessage("Please provide a valid price.");
    if (parseFloat(price) < parseFloat(ad?.price))
      return setValidationMessage(
        "Your bid must be higher then bid opening price.",
      );

    setValidationMessage("");
    setShowConfirmModal(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmModal(false);
    if (!ad || !ad?._id || !price) return;

    setIsLoading(true);
    try {
      await createBid.mutateAsync({ price, adId: ad?._id });
    } catch (err: any) {
      setErrorMessage(err?.message || "Request failed");
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setShowSuccessModal(true);
    setValidationMessage("");
    setPrice("");
    setAcceptedTerms(false);
    onSuccess();
  };

  const tz = user?.timezone || ad?.timezone;

  const isPriceValid =
    !!price && parseFloat(price) >= parseFloat(ad?.price || "0");
  const canSubmit = isPriceValid && acceptedTerms;

  return (
    <>
      <Modal
        className="placeBidModal"
        size="lg"
        show={show}
        onHide={onHide}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton className="placeBidModal__header">
          <Modal.Title className="placeBidModal__heading">
            Place your bid
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="placeBidModal__body">
          <div className="adDetails__panel placeBidModal__panel">
            <div className="adDetails__topRow">
              <span className="adDetails__status adDetails__status--live">
                <span className="adDetails__statusDot" /> Live ·{" "}
                {Utils.formatFromNow(ad?.biddingEnds)}
              </span>
              {ad?.categoryId?.name && (
                <span className="adDetails__categoryChip">
                  {ad?.categoryId?.name}
                  {ad?.subCategoryId?.name
                    ? ` · ${ad?.subCategoryId?.name}`
                    : ""}
                </span>
              )}
            </div>

            <h1 className="adDetails__title">{ad?.title}</h1>

            <div className="adDetails__viewsRow">
              <span>
                <i className="bi bi-eye" aria-hidden="true" />
                {parseInt(ad?.views) === 0
                  ? 1
                  : Utils.formatToNumber(ad?.views)}{" "}
                views
              </span>
            </div>

            {ad?.description && (
              <p className="adDetails__description">{ad?.description}</p>
            )}

            <div className="adDetails__metaGrid">
              {ad?.mediumId?.name && (
                <div className="adDetails__metaTile">
                  <div className="adDetails__metaLabel">Medium</div>
                  <div className="adDetails__metaValue">
                    {ad?.mediumId?.name}
                  </div>
                </div>
              )}
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">Dimensions</div>
                <div className="adDetails__metaValue">
                  {ad?.height}″ × {ad?.width}″ × {ad?.depth}″
                </div>
              </div>
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">Weight</div>
                <div className="adDetails__metaValue">{ad?.weight} lb</div>
              </div>
              <div className="adDetails__metaTile">
                <div className="adDetails__metaLabel">Closes</div>
                <div className="adDetails__metaValue">
                  {Utils.formatDateTimeInTz(ad?.biddingEnds, tz)}
                </div>
              </div>
            </div>

            <div className="adDetails__priceBlock placeBidModal__priceBlock">
              <div className="adDetails__priceLeft">
                <div className="adDetails__priceLabel">Opening bid</div>
                <div className="adDetails__priceValue">
                  {Utils.formatToCurrency(ad?.price)}
                </div>
              </div>
              {quote && (
                <div className="adDetails__priceRight">
                  <div className="adDetails__priceLabel">Est. shipping</div>
                  <div className="placeBidModal__shipping">
                    {Utils.formatToCurrency(quote?.total)}
                  </div>
                </div>
              )}
            </div>

            {validationError && (
              <div className="placeBidModal__warning">
                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                <span>{validationError}</span>
              </div>
            )}

            <div className="placeBidModal__info">
              <i className="bi bi-info-circle" aria-hidden="true" />
              <div>
                Heads up: If you win, additional fees apply on top of your bid.{" "}
                <strong>Arta</strong> handles shipping (estimate shown above)
                and is added at checkout. <strong>Trustap</strong>, our payment
                partner, applies a buyer protection fee as follows:
                <ul className="placeBidModal__feeList">
                  <li>
                    <strong>5%</strong> of purchase price on credit card
                    payments (immediately processed)
                  </li>
                  <li>
                    <strong>1.5%</strong> of purchase price on bank transfers
                    (takes up to 3&ndash;4 business days)
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="placeBidModal__bidSection">
            <div className="placeBidModal__bidLabel">
              <i className="bi bi-hammer" aria-hidden="true" />
              <span>Your highest bid</span>
            </div>
            <div className="placeBidModal__bidInputWrap">
              <span className="placeBidModal__currencyPrefix">$</span>
              <Number
                placeholder="Enter your highest bid"
                name="bidPrice"
                value={price}
                onChange={(e: any) => setPrice(e?.target?.value)}
                thousandSeparator={true}
              />
            </div>
            <p className="placeBidModal__bidHint">
              Must be at least {Utils.formatToCurrency(ad?.price)} (the opening
              bid).
            </p>
          </div>

          {validationMessage && (
            <div className="placeBidModal__error">
              <i className="bi bi-x-circle" aria-hidden="true" />
              <span>{validationMessage}</span>
            </div>
          )}

          <Form.Check
            type="checkbox"
            id="placeBidAcceptTerms"
            className="termsCheck mt-3"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            label={
              <>
                I have read and agree to the{" "}
                <Link
                  to="/bidding-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Bidding Terms &amp; Conditions
                </Link>
              </>
            }
          />
        </Modal.Body>
        <Modal.Footer className="placeBidModal__footer">
          <button
            type="button"
            className="placeBidModal__cancel"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="placeBidModal__submit"
            onClick={handleSubmitClick}
            disabled={!canSubmit}
          >
            <i className="bi bi-hammer" aria-hidden="true" />
            <span>Submit Your Bid</span>
          </button>
        </Modal.Footer>
      </Modal>
      <Confirm
        show={showConfirmModal}
        variant="primary"
        iconClass="bi-hammer"
        title="Submit this bid?"
        body={`Are you sure you want to submit a bid of ${Utils.formatToCurrency(
          price,
        )} for "${ad?.title}"? Please note: you won't be able to change this bid to a lower amount later.`}
        closeButtonText="Go Back"
        confirmButtonText="Yes, Submit Bid"
        onNo={() => setShowConfirmModal(false)}
        onYes={handleConfirmedSubmit}
      />
      <CommonModal
        title="Success!"
        message="Your bid is place successfully! We will let you know by emails if you win the bid."
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
      />
    </>
  );
};

export default PlaceYourBidModal;
